import { CategoryBadge } from '@components/CategoryBadge';
import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import LazyVideo from '@components/LazyVideo';
import { useLowDataMode } from '@hooks/station';
import { memo } from 'react';
import { placeHolderData } from '../../config';

interface ContributorCardProps {
  contributor: any;
  videoUrl?: string;
  posterUrl?: string;
  name?: string;
  onClick?: (e?: React.MouseEvent) => void;
  onFavoriteClick?: (e: React.MouseEvent) => void;
  showFavoriteButton?: boolean;
  isLoading?: boolean;
  isFavorited?: boolean;
  callToAction?: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
}

export const ContributorCard = memo(
  ({
    videoUrl,
    posterUrl,
    name,
    onClick,
    onFavoriteClick,
    showFavoriteButton = false,
    isLoading = false,
    isFavorited = false,
    callToAction,
    jobTitle,
  }: ContributorCardProps) => {
    const [isLowDataMode] = useLowDataMode();
    const displayVideoUrl =
      videoUrl || (posterUrl ? '' : placeHolderData.contributorMobileVideo);
    const displayPosterUrl =
      posterUrl || placeHolderData.contributorThumbnail || '';
    const displayName = name || '';

    // Check if we should use companion-like design
    const hasCompanionData = callToAction;
    const displayCallToAction = callToAction || '';
    const displayJobTitle = jobTitle || '';

    const handleVideoLoad = (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const videoEl = e.currentTarget;
      videoEl.classList.remove('opacity-0');
    };

    return (
      <div
        className="w-full h-[520px] snap-start relative rounded-[16px] shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] cursor-pointer hover:shadow-[0_4px_20px_0_rgba(0,0,0,0.50)] transition-all duration-300 opacity-100"
        onClick={onClick}>
        <CategoryBadge category="companion" />
        {/* Background Video */}
        <div className="flex absolute top-0 left-0 w-full h-full rounded-[16px] overflow-hidden">
          {isLowDataMode ? (
            <img
              className="w-full h-full object-cover"
              src={displayPosterUrl}
              alt={displayName}
            />
          ) : (
            displayVideoUrl && (
              <LazyVideo
                className="w-full h-full object-cover"
                src={displayVideoUrl}
                poster={displayPosterUrl || undefined}
                autoPlay
                loop
                muted
                playsInline
                webkit-playsinline="true"
                preload="metadata"
                onLoadedData={handleVideoLoad}
                enabled
                rootMargin="400px"
                threshold={0.1}
              />
            )
          )}
          {/* {!displayVideoUrl && displayPosterUrl && (
            <img
              className="w-full h-full object-cover"
              src={displayPosterUrl}
              alt={`${displayName}`}
            />
          )} */}
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_34.69%,rgba(0,0,0,0.9)_100%)] z-[99] rounded-[16px]" />

        {/* Bottom Section - Companion-like design when call_to_action and name available */}
        {hasCompanionData ? (
          <div className="absolute gap-[10px] z-[100] left-0 right-0 mx-auto my-0 h-fit flex flex-col justify-center items-start rounded-[16px] px-5 pb-5 w-full -bottom-1">
            {/* Call to Action */}
            <div className="text-white text-[20px] md:text-[24px] text-left font-normal leading-[30px] w-full font-[DM_Serif_Display] tracking-[0%] align-middle mb-[9px]">
              {displayCallToAction}
            </div>

            {/* Name and Job Title */}
            <div className="flex items-center gap-[6px]">
              <span className="text-white font-[DM_Sans] font-medium text-[16px] leading-[100%] tracking-[-1%] align-middle">
                {name}
              </span>
              {displayJobTitle && (
                <>
                  <span className="text-[15px] text-[#4A4A4A] w-[4px] h-[4px] rounded-full bg-[#4A4A4A]"></span>
                  <span className="text-[#7D7D7D] font-[DM_Sans] font-[400] text-[16px] leading-[15px] tracking-[0%] align-middle">
                    {displayJobTitle}
                  </span>
                </>
              )}
            </div>

            {/* Favorite Button - Only show if showFavoriteButton is true */}
            {showFavoriteButton && onFavoriteClick && (
              <div
                className="absolute top-5 right-5 flex items-center gap-1 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={onFavoriteClick}>
                {isFavorited ? (
                  <Likedicon width={20} height={21} />
                ) : (
                  <FavoriteIcon width={20} height={21} />
                )}
              </div>
            )}
          </div>
        ) : (
          /* Original design - Contributor Name and Like Button */
          <div className="absolute z-[100] bottom-0 left-0 right-0 mx-auto my-0 h-fit flex flex-row justify-between items-end rounded-[16px] px-5 pb-5 w-full">
            {/* Name - Using call-to-action typography */}
            <div className="text-white text-[20px] md:text-[24px] text-left font-normal leading-[30px] font-[DM_Serif_Display] tracking-[0%] flex-1 pr-4">
              {displayName}
            </div>

            {/* Favorite Button - Only show if showFavoriteButton is true */}
            {showFavoriteButton && onFavoriteClick && (
              <div
                className="flex items-center gap-1 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={onFavoriteClick}>
                {isFavorited ? (
                  <Likedicon width={20} height={21} />
                ) : (
                  <FavoriteIcon width={20} height={21} />
                )}
              </div>
            )}
          </div>
        )}

        {isLoading && (
          <div className="absolute z-[100] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  },
);

ContributorCard.displayName = 'ContributorCard';
